import type { UsageAction } from "../../usage/events";

/** Fire-and-forget owner usage tracker. Never throws, never retries, never blocks the UI. */

const ENDPOINT = "/api/usage/events";
const FLUSH_MS = 30_000;
const HEARTBEAT_MS = 60_000;
const INPUT_WINDOW_MS = 2 * 60_000;
const VISIT_IDLE_MS = 30 * 60_000;
const MAX_QUEUE = 200;
const MAX_BATCH = 50;
const POINTERMOVE_THROTTLE_MS = 5_000;
const UUID_SEGMENT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Path without query/hash, Boulevard ids masked, at most 200 chars. */
export function routePattern(pathname: string): string {
  return pathname.split(/[?#]/)[0].replace(UUID_SEGMENT, ":blvdId").slice(0, 200);
}

export interface TrackerEnv {
  now(): number;
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(h: unknown): void;
  isVisible(): boolean;
  onVisibilityHidden(fn: () => void): void;
  onInput(fn: () => void): void;
  sendBeacon?: (url: string, body: Blob) => boolean;
  fetch: typeof fetch;
  randomId(): string;
}

type Props = Record<string, string | number | boolean>;
interface QueuedEvent {
  type: "page_view" | "heartbeat" | "action";
  visitId: string;
  at: string;
  path?: string;
  name?: UsageAction;
  props?: Props;
}

const MAX_PROPS_BYTES = 1024;

export type Tracker = {
  pageView(pathname: string): void;
  action(name: UsageAction, props?: Props): void;
  flush(): void;
  stop(): void;
};
const NOOP_TRACKER: Tracker = { pageView() {}, action() {}, flush() {}, stop() {} };

/** Never throws: a construction failure yields a tracker that does nothing. */
export function createTracker(env: TrackerEnv, opts: { csrf: string }): Tracker {
  try {
    return buildTracker(env, opts);
  } catch {
    return NOOP_TRACKER;
  }
}

function buildTracker(env: TrackerEnv, opts: { csrf: string }): Tracker {
  let queue: QueuedEvent[] = [];
  let visitId = env.randomId();
  let lastActivity = env.now();
  let lastInput: number | null = null;
  let path: string | undefined;
  let stopped = false;
  let hiddenSinceActivity = false;

  const safe = (fn: () => void) => () => { try { if (!stopped) fn(); } catch { /* tracking never affects the app */ } };

  /** Any sign of life: rotates the visit after 30 idle minutes and opens the 2-minute heartbeat window. */
  const touch = () => {
    const now = env.now();
    if (hiddenSinceActivity || now - lastActivity > VISIT_IDLE_MS) visitId = env.randomId();
    hiddenSinceActivity = false;
    lastActivity = now;
    lastInput = now;
  };

  const push = (e: Omit<QueuedEvent, "visitId" | "at">) => {
    queue.push({ ...e, visitId, at: new Date(env.now()).toISOString() });
    while (queue.length > MAX_QUEUE) queue.shift();
  };

  const send = (events: QueuedEvent[]) => {
    const blob = new Blob([JSON.stringify({ csrf: opts.csrf, events })], { type: "application/json" });
    try {
      if (env.sendBeacon && env.sendBeacon(ENDPOINT, blob)) return;
    } catch { /* fall through to fetch */ }
    try {
      env.fetch(ENDPOINT, { method: "POST", body: blob, keepalive: true, credentials: "same-origin" }).catch(() => {});
    } catch { /* ignore */ }
  };

  const flush = safe(() => {
    const events = queue;
    queue = [];
    for (let i = 0; i < events.length; i += MAX_BATCH) send(events.slice(i, i + MAX_BATCH));
  });

  const flushTimer = env.setInterval(flush, FLUSH_MS);
  const heartbeatTimer = env.setInterval(safe(() => {
    if (!env.isVisible() || lastInput === null || env.now() - lastInput > INPUT_WINDOW_MS) return;
    push({ type: "heartbeat", ...(path ? { path } : {}) });
  }), HEARTBEAT_MS);
  env.onVisibilityHidden(safe(() => { hiddenSinceActivity = true; flush(); }));
  env.onInput(safe(touch));

  return {
    pageView: (pathname: string): void => safe(() => {
      touch();
      path = routePattern(pathname);
      push({ type: "page_view", path });
    })(),
    action: (name: UsageAction, props?: Props): void => safe(() => {
      touch();
      const copy = props ? { ...props } : undefined;
      const keep = copy && new Blob([JSON.stringify(copy)]).size <= MAX_PROPS_BYTES;
      push({ type: "action", name, ...(keep ? { props: copy } : {}) });
    })(),
    flush,
    stop: (): void => {
      try {
        if (stopped) return;
        flush();
        stopped = true;
        env.clearInterval(flushTimer);
        env.clearInterval(heartbeatTimer);
      } catch { /* ignore */ }
    },
  };
}

/** The real browser environment: window/document/navigator, with input listeners removed on stop. */
export function startUsageTracking(opts: { csrf: string }): Tracker {
  const cleanups: Array<() => void> = [];
  const listen = (target: EventTarget, type: string, fn: () => void) => {
    target.addEventListener(type, fn, { passive: true });
    cleanups.push(() => target.removeEventListener(type, fn));
  };
  const env: TrackerEnv = {
    now: () => Date.now(),
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (h) => window.clearInterval(h as number),
    isVisible: () => document.visibilityState === "visible",
    onVisibilityHidden: (fn) => {
      listen(document, "visibilitychange", () => { if (document.visibilityState === "hidden") fn(); });
      listen(window, "pagehide", fn);
    },
    onInput: (fn) => {
      for (const type of ["pointerdown", "keydown", "scroll"]) listen(window, type, fn);
      let last = 0;
      listen(window, "pointermove", () => {
        const now = Date.now();
        if (now - last < POINTERMOVE_THROTTLE_MS) return;
        last = now;
        fn();
      });
    },
    sendBeacon: typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function"
      ? (url, body) => navigator.sendBeacon(url, body)
      : undefined,
    fetch: (...args) => window.fetch(...args),
    randomId: randomUuid,
  };
  try {
    return wire(env, opts, cleanups);
  } catch {
    for (const off of cleanups.splice(0)) { try { off(); } catch { /* ignore */ } }
    return NOOP_TRACKER;
  }
}

function randomUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));
}

function wire(env: TrackerEnv, opts: { csrf: string }, cleanups: Array<() => void>): Tracker {
  const tracker = buildTracker(env, opts);
  return {
    ...tracker,
    stop: () => {
      tracker.stop();
      for (const off of cleanups.splice(0)) off();
    },
  };
}
